'use client';

import { Box } from '@ironhack/design-system2/components/box';
import { Flex } from '@ironhack/design-system2/components/flex';
import { Icon } from '@ironhack/design-system2/components/icon';
import { Text } from '@ironhack/design-system2/components/text';
import { useBreakpointValue } from '@ironhack/design-system2/hooks';
import { X } from '@ironhack/design-system2/icons/x';
import { ChevronLeft } from '@ironhack/design-system2/icons/chevron-left';
import {
  type PropsWithChildren,
  type ReactElement,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { useClickAway } from 'react-use';

import { useIsClient } from '@/app/components/client/client-only';
import { IronhackLogoLink } from '@/app/components/client/ironhack-logo-link';
import { NavFormButtons } from '../nav-form-buttons';
import { NavbarContext } from '../navbar-context';
import { type NavbarQuery } from '../types';

type Props = PropsWithChildren<{
  data: NavbarQuery;
}>;

const useBodyScrollLock = (isLocked: boolean) => {
  useEffect(() => {
    document.body.style.overflow = isLocked ? 'hidden' : '';

    return () => {
      document.body.style.overflow = '';
    };
  }, [isLocked]);
};

export const NavMenuWrapper = (props: Props): ReactElement => {
  const { children, data } = props;

  const isClient = useIsClient();
  const isHamburgerMenu = useBreakpointValue({ base: true, lg: false });

  const {
    closeModalMenu,
    goBack,
    isFirstLevel,
    isModalMenuOpen,
    modalLevelTitle,
    resetMenuLevel,
  } = useContext(NavbarContext);

  // Close the expanded menu when clicking away from the navbar
  const navbarRef = useRef(null);
  useClickAway(navbarRef, resetMenuLevel);
  const isDesktopMenuOpen = !isFirstLevel;
  useBodyScrollLock(isModalMenuOpen);

  if (isClient && isHamburgerMenu && isModalMenuOpen) {
    return (
      <Box
        bg="brand.secondary"
        color="text.inverse"
        height="100%"
        left={0}
        overflow="auto"
        position="fixed"
        top={0}
        w="full"
        zIndex={10}
      >
        <Flex
          align="center"
          as={Flex}
          bg="brand.secondary"
          justify="space-between"
          pb={isFirstLevel ? 6 : 7}
          position="sticky"
          pt={3}
          px={[2, null, 7]}
          top={0}
        >
          {isFirstLevel ? (
            // Set height to text line height to avoid layout shift
            <IronhackLogoLink data={data} h="28px" />
          ) : (
            <>
              <Icon
                as={ChevronLeft}
                boxSize="24px"
                cursor="pointer"
                onClick={goBack}
              />
              <Text textStyle="xlBold">{modalLevelTitle}</Text>
            </>
          )}
          <Icon
            as={X}
            boxSize="24px"
            cursor="pointer"
            onClick={closeModalMenu}
          />
        </Flex>
        <Box mb={3}>{children}</Box>
        {isFirstLevel && (
          // This position absolute is due to sticky header
          <Box bottom={0} pb={3} position="absolute" w="full">
            <Box as={Flex} direction="column" gap={2} px={[2, null, 7]} py={0}>
              <NavFormButtons data={data} id="navMobileCTAs" w="full" />
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      {...(isDesktopMenuOpen && { bg: [null, null, null, 'brand.secondary'] })}
      // Hide in mobile for the server render
      display={['none', null, null, 'block']}
      // For easily absolute positioning desktop menu
      position={[null, null, null, 'relative']}
      ref={navbarRef}
    >
      {children}
    </Box>
  );
};
